import jwt from 'jwt-decode';

import {
  admin,
  blobStorage,
  clampingTools,
  dashboard,
  device,
  erp,
  imageHandling,
  machineOperation,
  machines,
  manufacture,
  membership,
  productionInformation,
  productionOrders,
  search,
  statistics,
  ticket,
  time,
  tools,
  user,
} from './modules';

import { getSecureValue, isWeb } from '@helpers';
import type { ApiConfig } from './types';

export const baseURLIndentitiServer = `https://login.smarfac.com/da4d98ba-ece3-4f6a-88d7-b2fc4eaa1d64/B2C_1_sign_signup_ROPC`;

// SF-4988 - commented out, subdomain is now always used on web for environment detection
// The original logic only checked subdomain on anonymous pages. On
// authenticated pages, it read the JWT extension_Environment claim - which could be WRONG after B2C SSO
// cross-environment login (for example, logging into QA after a prod B2C session).
// const anonymLinks = [
//   'connect',
//   'user-password',
//   'confirm-invite',
//   'factory-time',
//   'factory-language',
//   'connector-linked',
//   'qr',
//   'forgot-password',
//   'confirm-code',
//   'user-email',
//   'connector',
//   'welcome',
//   'login',
//   'signin-oidc',
//   'user-details',
//   'registration',
//   'download-connector',
// ];

export type Environment = 'dev' | 'qa' | 'stg' | 'prod';

// B2C extension_Environment claim values mapped to app environment identifiers.
// Used by both mobile (JWT decode) and web (MSAL AccountInfo claims).
export const B2C_ENV_MAP: Record<string, Environment> = {
  Development: 'dev',
  QA: 'qa',
  Staging: 'stg',
  Production: 'prod',
};

interface JwtPayload {
  extension_Environment?: string;
  [key: string]: unknown;
}

export const detectEnvironment = (): Environment | false => {
  try {
    if (isWeb) {
      // After login, env is set from B2C JWT claims (via setMsalSession) or
      // validated by the backend (via generateTokenInfo). Trust it when available
      // — it correctly handles cross-environment B2C SSO (e.g., QA user on dev subdomain).
      const backendEnv = getSecureValue('env') as Environment;
      if (backendEnv) return backendEnv;

      // Fallback to subdomain for initial/anonymous requests before env is set
      const host = window.location.host;
      const subdomain = host.split('.')[0];
      const envList: Record<string, Environment> = {
        dev: 'dev',
        qa: 'qa',
        preview: 'stg',
        smarfac: 'prod',
        www: 'prod',
        app: 'prod',
        'localhost:3030': (process.env.ENV as Environment) || 'dev',
      };
      return envList[subdomain] || false;
    }

    // Mobile: use JWT claim as primary source
    const token = getSecureValue('token');
    if (token) {
      try {
        const tokenInfo = jwt<JwtPayload>(token);
        if (tokenInfo && tokenInfo.extension_Environment) {
          const tokenEnv = B2C_ENV_MAP[tokenInfo.extension_Environment];
          if (tokenEnv) {
            return tokenEnv;
          }
        }
      } catch (e) {
        // Token decode failed, continue to fallback
      }
    }

    const storageEnv = getSecureValue('env');
    if (storageEnv) {
      return storageEnv as Environment;
    }

    return false;
  } catch (e) {
    return false;
  }
};

// Cache for API instance to avoid recreating on every call
let cachedApi: ReturnType<typeof createApi> | null = null;
let cachedEnv: Environment | false | null = null;

// Invalidate cached API instance if the environment has changed.
// Called after generateTokenInfo sets the backend-validated env.
export const invalidateApiCache = (): void => {
  const currentEnv = detectEnvironment();
  if (cachedEnv !== currentEnv) {
    cachedApi = null;
    cachedEnv = null;
  }
};

const createApi = (data: ApiConfig, appHubURL: string, signalRMembershipOnboardingURL: string) => ({
  baseURL: data.baseURL,
  appHubURL,
  signalRMembershipOnboardingURL,
  productionOrders: productionOrders(data),
  user: user(data),
  device: device(data),
  imageHandling: imageHandling(data),
  machineOperation: machineOperation(data),
  time: time(data),
  manufacture: manufacture(data),
  membership: membership(data),
  ticket: ticket(data),
  tools: tools(data),
  clampingTools: clampingTools(data),
  machines: machines(data),
  blobStorage: blobStorage(data),
  search: search(data),
  statistics: statistics(data),
  dashboard: dashboard(data),
  admin: admin(data),
  erp: erp(data),
  productionInformation: productionInformation(data),
});

export type Api = ReturnType<typeof createApi>;

const get = (): Api => {
  const env = detectEnvironment();

  // Return cached API if environment hasn't changed
  if (cachedApi && cachedEnv === env) {
    return cachedApi;
  }

  const envList: Record<Environment, string> = {
    dev: 'gateway-dev',
    qa: 'gateway-qa',
    stg: 'gateway-stg',
    prod: 'gateway',
  };

  let baseENV = envList[(process.env.ENV as Environment) || 'dev'];
  if (env && envList[env]) {
    baseENV = envList[env];
  }

  const baseURL = `https://${baseENV}.smarfac.com/api`;
  const baseURLoData = `https://${baseENV}.smarfac.com`;
  const appHubURL = `https://${baseENV}.smarfac.com/pushapi/v2/apphub`;
  const signalRMembershipOnboardingURL = `https://${baseENV}.smarfac.com/pushapi/v2/onboardinghub`;

  // Data object passed to API modules (matches original JS structure)
  const data: ApiConfig = {
    baseENV,
    baseURLoData,
    baseURL,
    baseURLIndentitiServer,
  };

  const api = createApi(data, appHubURL, signalRMembershipOnboardingURL);

  // Cache the API and environment
  cachedApi = api;
  cachedEnv = env;

  return api;
};

export default get;
